<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <!-- Header -->
        <div
            class="flex flex-wrap items-center justify-between gap-2 px-5 py-3"
        >
            <div class="flex min-w-0 items-center gap-2.5">
                <div class="relative shrink-0">
                    <span
                        v-if="loading"
                        class="cb-risk-orbit pointer-events-none absolute -inset-1.5 rounded-full"
                        aria-hidden="true"
                    />
                    <component
                        :is="headerIcon"
                        class="relative h-5 w-5 shrink-0"
                        :class="headerIconClass"
                    />
                </div>
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                    Risco de Chargeback
                </h3>
            </div>

            <div class="flex flex-wrap items-center gap-1.5">
                <div
                    v-if="loading"
                    class="flex items-center gap-2"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <span class="relative flex h-1.5 w-1.5">
                        <span
                            class="cb-risk-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"
                        />
                        <span
                            class="relative inline-flex h-1.5 w-1.5 rounded-full bg-indigo-500"
                        />
                    </span>
                    <span
                        class="font-mono text-[11px] tabular-nums text-gray-500 dark:text-gray-400"
                    >
                        {{ currentLoadingStep.label }}
                    </span>
                </div>

                <template v-else-if="risk">
                    <span
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="levelBadgeClass"
                    >
                        {{ levelLabel }}
                    </span>

                    <span
                        v-if="risk.exit && risk.exit.level !== 'ativo'"
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="exitBadgeClass"
                    >
                        {{ exitLabel }}
                    </span>

                    <span
                        v-if="risk.tail && risk.tail.level !== 'normal'"
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="tailBadgeClass"
                    >
                        {{ tailLabel }}
                    </span>

                    <span
                        v-if="risk.route === 'revisao_humana'"
                        v-tooltip="
                            'Penalidade severa em seller de volume alto: sem bloqueio automático, a decisão é sempre humana'
                        "
                        class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-slate-700 dark:text-slate-200"
                    >
                        Revisão humana
                    </span>

                    <span
                        v-if="risk.flags && risk.flags.global_watchlist"
                        class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                    >
                        Watchlist 12m
                    </span>
                </template>

                <span
                    v-else-if="error"
                    class="text-xs text-gray-400 dark:text-gray-500"
                >
                    indisponível
                </span>
            </div>
        </div>

        <div
            v-if="loading"
            class="cb-risk-loader border-t border-gray-100 dark:border-gray-700/50"
        >
            <div
                class="relative h-0.5 overflow-hidden bg-gray-100 dark:bg-gray-700"
            >
                <div class="cb-risk-scan-bar" />
            </div>

            <p class="px-5 pt-3 text-[11px] text-gray-400 dark:text-gray-500">
                {{ currentLoadingStep.hint }}
            </p>

            <div
                class="grid grid-cols-2 gap-x-6 gap-y-3 px-5 py-3 sm:grid-cols-4"
            >
                <div
                    v-for="(step, index) in loadingSteps"
                    :key="step.label"
                    class="cb-risk-step"
                    :class="{ 'is-active': loadingStep === index }"
                >
                    <p
                        class="text-[10px] font-medium uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500"
                    >
                        {{ step.label }}
                    </p>
                    <div
                        class="mt-2 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
                    >
                        <div
                            :key="
                                loadingStep === index
                                    ? `on-${loadingStep}`
                                    : `off-${index}`
                            "
                            class="cb-risk-step-bar h-full rounded-full"
                        />
                    </div>
                </div>
            </div>

            <div class="px-5 pb-3">
                <svg
                    class="block h-6 w-full text-indigo-400 dark:text-indigo-300"
                    viewBox="0 0 100 24"
                    preserveAspectRatio="none"
                    role="img"
                    aria-hidden="true"
                >
                    <path
                        d="M0,18 C12,16 18,8 25,10 C32,12 38,20 50,14 C62,8 70,6 78,12 C86,18 92,10 100,8"
                        class="fill-current opacity-10"
                    />
                    <path
                        d="M0,18 C12,16 18,8 25,10 C32,12 38,20 50,14 C62,8 70,6 78,12 C86,18 92,10 100,8"
                        fill="none"
                        class="cb-risk-spark-line stroke-current"
                        stroke-width="1.4"
                        stroke-linejoin="round"
                        vector-effect="non-scaling-stroke"
                    />
                </svg>
            </div>
        </div>

        <!-- Veredito -->
        <div
            v-if="risk && !loading && verdict"
            class="border-t px-5 py-2.5"
            :class="verdictToneClass"
        >
            <div class="flex items-start gap-2">
                <ExclamationTriangleIcon
                    v-if="verdict.tone === 'danger'"
                    class="mt-0.5 h-4 w-4 shrink-0 text-red-500 dark:text-red-400"
                />
                <InformationCircleIcon
                    v-else-if="verdict.tone === 'muted'"
                    class="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
                />
                <p class="text-xs font-semibold" :class="verdictTextClass">
                    {{ verdict.text }}
                </p>
            </div>
        </div>

        <!-- KPIs de decisão -->
        <div
            v-if="risk && !loading"
            class="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-100 px-5 py-3 dark:border-gray-700/50 sm:grid-cols-4"
        >
            <div
                v-if="risk.coverage"
                v-tooltip="
                    '(Fica na conta + reserva) sobre o cartão 90d: se até esse percentual das vendas de cartão ainda contestáveis virar disputa, o prejuízo sai do retido do seller. Taxa real costuma ficar em 1 a 3 por cento'
                "
            >
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Cobertura pós-saque
                </p>
                <p
                    class="font-mono text-sm font-semibold"
                    :class="coverageClass"
                >
                    {{
                        risk.coverage.coverage_pct === null
                            ? "n/a"
                            : formatPercent(risk.coverage.coverage_pct)
                    }}
                    <span
                        class="ml-1 text-[10px] font-normal text-gray-400 dark:text-gray-500"
                    >
                        atenção &lt; 25% · crítico &lt; 10%
                    </span>
                </p>
                <!-- Exposição absoluta à mão: % alta de disputa sobre cartão 90d
                     pequeno é pouco dinheiro em risco, e é isso que decide. -->
                <p
                    class="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-400"
                >
                    contra
                    {{ formatCurrency(risk.coverage.card_90d_amount) }} de
                    cartão 90d
                </p>
                <div
                    v-if="risk.coverage.coverage_pct !== null"
                    class="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
                >
                    <div
                        class="h-full rounded-full transition-all"
                        :class="coverageBarClass"
                        :style="{ width: coverageBarWidth }"
                    />
                </div>
            </div>

            <div v-if="risk.coverage">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Este saque
                </p>
                <p
                    class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                >
                    {{ formatCurrency(risk.coverage.cashout_amount) }}
                </p>
                <p
                    class="mt-0.5 font-mono text-[11px]"
                    :class="
                        risk.coverage.retained_after_cashout <= 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-500 dark:text-gray-400'
                    "
                    v-tooltip="
                        'Disponível (descontada a reserva, que no Smaug vive dentro dele) + Pendente. Este saque já saiu do disponível ao ser solicitado'
                    "
                >
                    fica
                    {{ formatCurrency(risk.coverage.retained_after_cashout) }}
                    <template v-if="risk.coverage.reserved > 0">
                        · +{{ formatCurrency(risk.coverage.reserved) }} reserva
                    </template>
                </p>
            </div>

            <div
                v-if="risk.exposure"
                v-tooltip="
                    'Pior entre contagem e valor: disputas recebidas nos últimos 30 dias (inclusive de vendas antigas) sobre as vendas de cartão dos últimos 30 dias. Com poucas vendas de cartão na janela, o percentual infla e o alerta vira amarelo em vez de vermelho'
                "
            >
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Disputas na caixa 30d
                </p>
                <p
                    class="font-mono text-sm font-semibold"
                    :class="boxMetricClass"
                >
                    {{ formatPercent(risk.exposure.cb_box_30d) }}
                    <span
                        class="ml-1 text-[11px] font-normal text-gray-500 dark:text-gray-400"
                        v-tooltip="
                            'Disputas que entraram na janela sobre as vendas de cartão 30d (base da % por contagem)'
                        "
                    >
                        {{ risk.exposure.cb_box_30d_disputes }}/{{
                            risk.exposure.cb_box_30d_sales
                        }}
                    </span>
                    <span
                        class="ml-1 text-[10px] font-normal text-gray-400 dark:text-gray-500"
                    >
                        atenção ≥ 3% · crítico ≥ 5%
                    </span>
                </p>
                <p
                    v-if="risk.exposure.cb_box_30d_disputes > 0"
                    class="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-400"
                >
                    {{
                        formatCurrency(risk.exposure.cb_box_30d_disputes_amount)
                    }}
                    em disputa
                </p>
                <p
                    v-if="boxSmallBase && Number(risk.exposure.cb_box_30d) >= 3"
                    class="text-[10px] text-gray-400 dark:text-gray-500"
                >
                    % inflada: só {{ risk.exposure.cb_box_30d_sales }} vendas
                    cartão 30d
                    <template v-if="risk.card_share_90d !== null">
                        ({{ risk.card_share_90d }}% das vendas)
                    </template>
                </p>
                <p
                    v-else-if="
                        boxSingleDisputeByValue &&
                        Number(risk.exposure.cb_box_30d) >= 3
                    "
                    class="text-[10px] text-gray-400 dark:text-gray-500"
                >
                    % puxada pelo valor de 1 disputa única · por contagem
                    {{ formatPercent(risk.exposure.cb_box_30d_tpn) }}
                </p>
                <!-- Horizonte de eventos: cada barra é um dia; altura = valor em
                     disputa que ENTROU naquele dia. Rajada à direita (recente) é
                     o desenho que pede atenção; barra isolada à esquerda já está
                     precificada no %. Mesmo eixo de 30d da sparkline de vendas. -->
                <svg
                    v-if="boxSeries.length"
                    class="mt-1.5 block h-6 w-full text-rose-500 dark:text-rose-400"
                    viewBox="0 0 100 24"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label="Disputas por dia de entrada nos últimos 30 dias"
                >
                    <line
                        x1="0"
                        y1="23.5"
                        x2="100"
                        y2="23.5"
                        class="stroke-current opacity-20"
                        stroke-width="1"
                    />
                    <rect
                        v-for="(point, index) in boxSeries"
                        :key="point.date"
                        :x="index * (100 / boxSeries.length) + 0.4"
                        :width="100 / boxSeries.length - 0.8"
                        :y="24 - barHeight(point)"
                        :height="barHeight(point)"
                        class="fill-current"
                        :class="point.count > 0 ? 'opacity-90' : 'opacity-0'"
                    >
                        <title>{{ barTitle(point) }}</title>
                    </rect>
                </svg>
                <p
                    v-if="boxSeries.length && !boxSeriesHasEvents"
                    class="text-[10px] text-gray-400 dark:text-gray-500"
                >
                    nenhuma disputa entrou na janela
                </p>
            </div>

            <div v-if="risk.exit">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Vendas 30d
                </p>
                <p
                    class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                >
                    {{ formatCurrency(risk.exit.sales_30d_amount) }}
                    <span
                        v-if="
                            risk.exit.drop_pct !== null &&
                            risk.exit.drop_pct > 0
                        "
                        class="ml-1 text-xs font-semibold"
                        :class="
                            risk.exit.drop_pct >= 50
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-yellow-600 dark:text-yellow-400'
                        "
                    >
                        ▼{{ risk.exit.drop_pct }}%
                    </span>
                </p>
                <!-- Vendas diárias no mesmo eixo de 30d das disputas: a leitura
                     de decisão é o cruzamento (venda caindo + disputa chegando). -->
                <svg
                    v-if="salesSeries.length"
                    class="mt-1.5 block h-6 w-full text-indigo-400 dark:text-indigo-300"
                    viewBox="0 0 100 24"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label="Vendas pagas por dia nos últimos 30 dias"
                >
                    <path :d="salesAreaPath" class="fill-current opacity-10" />
                    <path
                        :d="salesLinePath"
                        fill="none"
                        class="stroke-current"
                        stroke-width="1.4"
                        stroke-linejoin="round"
                        vector-effect="non-scaling-stroke"
                    />
                </svg>
            </div>
        </div>

        <!-- Detalhes colapsáveis -->
        <div
            v-if="risk && !loading"
            class="border-t border-gray-100 dark:border-gray-700/50"
        >
            <div class="flex items-center justify-between gap-3 px-5 py-2">
                <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    @click="detailsOpen = !detailsOpen"
                >
                    <ChevronDownIcon
                        class="h-3.5 w-3.5 transition-transform"
                        :class="detailsOpen ? 'rotate-180' : ''"
                    />
                    {{ detailsOpen ? "Ocultar detalhes" : "Ver detalhes" }}
                </button>

                <span
                    v-if="risk.penalty < 0"
                    class="font-mono text-xs text-gray-500 dark:text-gray-400"
                >
                    penalidade v2: {{ risk.penalty }}
                </span>
            </div>

            <div
                v-if="detailsOpen"
                class="space-y-3 border-t border-gray-100 px-5 py-3 dark:border-gray-700/50"
            >
                <div class="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                    <div
                        v-tooltip="
                            'Taxa de disputa das vendas de cartão dos últimos 90 dias, pela data da venda'
                        "
                    >
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Disputas da safra 90d
                        </p>
                        <p
                            class="font-mono text-sm font-semibold"
                            :class="
                                metricClass(risk.exposure.cb_rate_90d, 2.5, 1)
                            "
                        >
                            {{ formatPercent(risk.exposure.cb_rate_90d) }}
                        </p>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500">
                            atenção ≥ 1% · crítico ≥ 2,5%
                        </p>
                    </div>
                    <div
                        v-tooltip="
                            'Taxa de disputa dos últimos 12 meses (pior entre contagem e valor)'
                        "
                    >
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Disputas 12m
                        </p>
                        <p
                            class="font-mono text-sm font-semibold"
                            :class="
                                metricClass(risk.exposure.cb_global_12m, 2.5, 1)
                            "
                        >
                            {{ formatPercent(risk.exposure.cb_global_12m) }}
                        </p>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500">
                            atenção ≥ 1% · crítico ≥ 2,5%
                        </p>
                    </div>
                    <div
                        v-tooltip="
                            'Participação de cartão nas vendas 90d. Pix não tem chargeback: com pouco cartão, as taxas ao lado explodem com uma disputa só'
                        "
                    >
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Cartão nas vendas
                        </p>
                        <p
                            class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                        >
                            {{
                                risk.card_share_90d === null
                                    ? "n/a"
                                    : formatPercent(risk.card_share_90d)
                            }}
                        </p>
                    </div>
                    <div
                        v-tooltip="
                            'Prazo realizado P: mediana ponderada por valor do tempo entre a venda e o dinheiro ficar disponível'
                        "
                    >
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Saque médio
                        </p>
                        <p
                            class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                        >
                            D+{{ risk.exposure.realized_cashout_days }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="risk.exit"
                    class="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3"
                >
                    <div>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            30d anteriores
                        </p>
                        <p
                            class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                        >
                            {{
                                formatCurrency(risk.exit.sales_prev_30d_amount)
                            }}
                        </p>
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Última venda
                        </p>
                        <p
                            class="font-mono text-sm font-semibold"
                            :class="
                                risk.exit.days_since_last_sale === null
                                    ? 'text-gray-900 dark:text-white'
                                    : metricClass(
                                          risk.exit.days_since_last_sale,
                                          21,
                                          7,
                                      )
                            "
                        >
                            {{ lastSaleLabel }}
                        </p>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500">
                            atenção ≥ 7d · crítico ≥ 21d
                        </p>
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Sacado 30d
                        </p>
                        <p
                            class="font-mono text-sm font-semibold"
                            :class="
                                risk.exit.withdrawn_30d_amount >
                                risk.exit.sales_30d_amount
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-gray-900 dark:text-white'
                            "
                        >
                            {{ formatCurrency(risk.exit.withdrawn_30d_amount) }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="risk.coverage || risk.suggested_reserve"
                    class="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-2"
                >
                    <div v-if="risk.coverage">
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Cartão 90d (janela de disputa)
                        </p>
                        <p
                            class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                        >
                            {{ formatCurrency(risk.coverage.card_90d_amount) }}
                        </p>
                    </div>
                    <div
                        v-if="risk.suggested_reserve"
                        v-tooltip="
                            'Cartão 90d multiplicado pela pior taxa conhecida (90d, global 12m ou 1% base da plataforma). É referência de tamanho do risco: não existe ação de reserva parcial por aqui, as alavancas são aprovar ou segurar o saque'
                        "
                    >
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Perda esperada
                        </p>
                        <p
                            v-if="
                                risk.suggested_reserve.reason ===
                                'baixa_exposicao'
                            "
                            class="font-mono text-sm font-semibold text-green-600 dark:text-green-400"
                        >
                            desprezível
                        </p>
                        <p
                            v-else
                            class="font-mono text-sm font-semibold text-gray-900 dark:text-white"
                        >
                            {{ formatCurrency(risk.suggested_reserve.amount) }}
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                            >
                                ({{ risk.suggested_reserve.rate_pct }}%)
                            </span>
                        </p>
                        <p
                            v-if="
                                risk.suggested_reserve.reason !==
                                'baixa_exposicao'
                            "
                            class="text-[10px] text-gray-400 dark:text-gray-500"
                        >
                            referência, sem ação automática
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import {
    ChevronDownIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ShieldCheckIcon,
} from "@heroicons/vue/24/outline";

export default {
    name: "ChargebackRiskBadge",
    components: {
        ChevronDownIcon,
        ExclamationTriangleIcon,
        InformationCircleIcon,
        ShieldCheckIcon,
    },
    props: {
        cashoutId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            error: false,
            risk: null,
            detailsOpen: false,
            loadingStep: 0,
            loadingTimer: null,
            loadingSteps: [
                {
                    label: "Cobertura",
                    hint: "Cruzando retido e reserva contra o cartão 90d",
                },
                {
                    label: "Este saque",
                    hint: "O que sai agora versus o que fica na conta",
                },
                {
                    label: "Disputas 30d",
                    hint: "O que ainda pode chegar contra o caixa recente",
                },
                {
                    label: "Vendas",
                    hint: "Se o seller está esfriando ou saindo",
                },
            ],
        };
    },
    computed: {
        currentLoadingStep() {
            return this.loadingSteps[this.loadingStep] ?? this.loadingSteps[0];
        },
        /**
         * Base pequena de cartão em 30d: o percentual do caixa infla com
         * poucas vendas no denominador, então o alerta não pinta de vermelho.
         * Espelha o espírito do guard de denominador (20 comissões em 90d).
         */
        boxSmallBase() {
            const sales = this.risk?.exposure?.cb_box_30d_sales;
            return sales !== undefined && Number(sales) < 10;
        },
        /**
         * Disputa única cuja % veio do valor (TPV > TPN): um ticket acima da
         * média em base de cartão pequena não é padrão estatístico. Trava o
         * KPI no amarelo e a nota mostra a % por contagem, que é a honesta.
         */
        boxSingleDisputeByValue() {
            const exposure = this.risk?.exposure;

            if (!exposure) {
                return false;
            }

            return (
                Number(exposure.cb_box_30d_disputes) === 1 &&
                Number(exposure.cb_box_30d_tpv) >
                    Number(exposure.cb_box_30d_tpn)
            );
        },
        boxMetricClass() {
            const value = this.risk?.exposure?.cb_box_30d;

            if (this.boxSmallBase || this.boxSingleDisputeByValue) {
                return this.metricClass(value, Infinity, 3);
            }

            return this.metricClass(value, 5, 3);
        },
        boxSeries() {
            return this.risk?.series?.box_disputes_daily ?? [];
        },
        boxSeriesHasEvents() {
            return this.boxSeries.some((point) => point.count > 0);
        },
        boxSeriesPeak() {
            return Math.max(...this.boxSeries.map((point) => point.amount), 0);
        },
        salesSeries() {
            return this.risk?.series?.sales_daily ?? [];
        },
        salesSeriesPeak() {
            return Math.max(
                ...this.salesSeries.map((point) => point.amount),
                0,
            );
        },
        salesLinePath() {
            return this.salesSeries
                .map((point, index) => {
                    const x =
                        this.salesSeries.length > 1
                            ? (index / (this.salesSeries.length - 1)) * 100
                            : 0;
                    const y =
                        this.salesSeriesPeak > 0
                            ? 23 - (point.amount / this.salesSeriesPeak) * 21
                            : 23;

                    return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
                })
                .join(" ");
        },
        salesAreaPath() {
            if (!this.salesSeries.length) {
                return "";
            }

            return `${this.salesLinePath} L100,24 L0,24 Z`;
        },
        headerIcon() {
            if (this.loading || this.error || !this.risk) {
                return ExclamationTriangleIcon;
            }

            if (this.risk.level === "baixo") {
                return ShieldCheckIcon;
            }

            return ExclamationTriangleIcon;
        },
        headerIconClass() {
            if (this.loading) {
                return "text-indigo-400 dark:text-indigo-300";
            }

            if (this.error || !this.risk) {
                return "text-gray-400 dark:text-gray-500";
            }

            const classes = {
                alto: "text-red-500 dark:text-red-400",
                medio: "text-yellow-500 dark:text-yellow-400",
                baixo: "text-green-500 dark:text-green-400",
                sem_base: "text-gray-400 dark:text-gray-500",
            };

            return classes[this.risk.level] || classes.sem_base;
        },
        levelBadgeClass() {
            const classes = {
                alto: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                medio: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
                baixo: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
                sem_base:
                    "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300",
            };
            return classes[this.risk?.level] || classes.sem_base;
        },
        levelLabel() {
            const labels = {
                alto: "Risco alto",
                medio: "Risco médio",
                baixo: "Risco baixo",
                sem_base: "Sem base",
            };
            return labels[this.risk?.level] || "Sem base";
        },
        exitBadgeClass() {
            const classes = {
                saindo: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                esfriando:
                    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
                ativo: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
                sem_historico:
                    "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300",
            };
            return classes[this.risk?.exit?.level] || classes.sem_historico;
        },
        exitLabel() {
            const labels = {
                saindo: "Saindo",
                esfriando: "Esfriando",
                ativo: "Vendas ativas",
                sem_historico: "Sem histórico",
            };
            return labels[this.risk?.exit?.level] || "Sem histórico";
        },
        tailBadgeClass() {
            const classes = {
                cauda_final:
                    "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                entrando_na_cauda:
                    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
            };
            return (
                classes[this.risk?.tail?.level] ||
                "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300"
            );
        },
        tailLabel() {
            const tail = this.risk?.tail;

            if (!tail) {
                return "";
            }

            if (tail.level === "cauda_final") {
                return "Cauda final";
            }

            return `Entrando na cauda (esvazia em ~${tail.drains_in_days}d)`;
        },
        coverageClass() {
            const pct = this.risk?.coverage?.coverage_pct;

            if (pct === null || pct === undefined) {
                return "text-gray-900 dark:text-white";
            }

            if (pct < 10) {
                return "text-red-600 dark:text-red-400";
            }

            if (pct < 25) {
                return "text-yellow-600 dark:text-yellow-400";
            }

            return "text-green-600 dark:text-green-400";
        },
        coverageBarClass() {
            const pct = this.risk?.coverage?.coverage_pct;

            if (pct === null || pct === undefined) {
                return "bg-gray-300 dark:bg-gray-600";
            }

            if (pct < 10) {
                return "bg-red-500";
            }

            if (pct < 25) {
                return "bg-yellow-500";
            }

            return "bg-green-500";
        },
        coverageBarWidth() {
            const pct = Number(this.risk?.coverage?.coverage_pct);

            if (Number.isNaN(pct)) {
                return "0%";
            }

            return `${Math.max(0, Math.min(pct, 100))}%`;
        },
        lastSaleLabel() {
            const days = this.risk?.exit?.days_since_last_sale;

            if (days === null || days === undefined) {
                return "nunca";
            }

            if (days === 0) {
                return "hoje";
            }

            return `há ${days}d`;
        },
        verdict() {
            if (!this.risk) {
                return null;
            }

            if (this.risk.combined_alert) {
                return {
                    tone: "danger",
                    text: "Exposição de chargeback com sinais de saída: vendas desacelerando e caixa recente ainda em disputa potencial. Analisar manualmente antes de liberar.",
                };
            }

            if (this.risk.tail_alert) {
                return {
                    tone: "danger",
                    text: `Cauda final: sem venda há ${this.risk.tail.days_since_last_sale} dias e o saldo é resto final, mas disputas de cartão 90d ainda podem chegar.`,
                };
            }

            if (this.risk.level === "sem_base") {
                return {
                    tone: "muted",
                    text: "Volume de cartão 90d insuficiente para estatística confiável. Decidir pelo contexto do seller, não pelas taxas.",
                };
            }

            if (this.risk.level === "baixo") {
                return {
                    tone: "ok",
                    text: "Sem sinais relevantes de chargeback e cobertura pós-saque confortável.",
                };
            }

            if (this.risk.level === "alto") {
                return {
                    tone: "danger",
                    text: "Exposição elevada de chargeback. Priorizar análise manual antes de liberar.",
                };
            }

            return {
                tone: "muted",
                text: "Sinais moderados de exposição. Conferir cobertura e tendência de vendas antes de liberar.",
            };
        },
        verdictToneClass() {
            const tone = this.verdict?.tone;

            if (tone === "danger") {
                return "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20";
            }

            return "border-gray-100 bg-gray-50/60 dark:border-gray-700/50 dark:bg-gray-900/30";
        },
        verdictTextClass() {
            const tone = this.verdict?.tone;

            if (tone === "danger") {
                return "text-red-700 dark:text-red-300";
            }

            if (tone === "ok") {
                return "text-gray-600 dark:text-gray-300";
            }

            return "text-gray-600 dark:text-gray-400";
        },
    },
    watch: {
        cashoutId: {
            immediate: true,
            handler() {
                this.detailsOpen = false;
                this.fetchRisk();
            },
        },
        loading(isLoading) {
            if (isLoading) {
                this.startLoadingTicker();
                return;
            }

            this.stopLoadingTicker();
        },
    },
    beforeUnmount() {
        this.stopLoadingTicker();
    },
    methods: {
        startLoadingTicker() {
            this.stopLoadingTicker();
            this.loadingStep = 0;
            this.loadingTimer = setInterval(() => {
                this.loadingStep =
                    (this.loadingStep + 1) % this.loadingSteps.length;
            }, 1100);
        },
        stopLoadingTicker() {
            if (!this.loadingTimer) {
                return;
            }

            clearInterval(this.loadingTimer);
            this.loadingTimer = null;
        },
        async fetchRisk() {
            if (!this.cashoutId) {
                return;
            }

            this.loading = true;
            this.error = false;
            this.risk = null;

            try {
                const response = await axios.get(
                    `/api/cashout/${this.cashoutId}/review/chargeback-risk`,
                );
                this.risk = response.data.data;
            } catch (error) {
                console.error("Erro ao carregar risco de chargeback:", error);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        formatPercent(value) {
            return `${Number(value || 0).toFixed(2)}%`;
        },
        /**
         * Altura mínima de 3px para dia com evento: uma disputa pequena num
         * seller de pico alto continua visível como evento no horizonte.
         */
        barHeight(point) {
            if (point.count === 0) {
                return 0;
            }

            if (this.boxSeriesPeak <= 0) {
                return 3;
            }

            return Math.max(3, (point.amount / this.boxSeriesPeak) * 21);
        },
        barTitle(point) {
            const date = new Date(`${point.date}T00:00:00`).toLocaleDateString(
                "pt-BR",
                {
                    day: "2-digit",
                    month: "2-digit",
                },
            );

            return `${date}: ${point.count} disputa(s) · ${this.formatCurrency(point.amount)}`;
        },
        formatCurrency(cents) {
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(Number(cents || 0) / 100);
        },
        metricClass(value, dangerAt, warnAt) {
            const number = Number(value || 0);

            if (number >= dangerAt) {
                return "text-red-600 dark:text-red-400";
            }

            if (number >= warnAt) {
                return "text-yellow-600 dark:text-yellow-400";
            }

            return "text-gray-900 dark:text-white";
        },
    },
};
</script>

<style scoped>
.cb-risk-orbit {
    border: 1.5px dashed rgb(99 102 241 / 0.55);
    animation: cb-risk-spin 1.6s linear infinite;
}

.cb-risk-ping {
    animation: cb-risk-ping 1.2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.cb-risk-scan-bar {
    position: absolute;
    inset: 0 auto 0 0;
    width: 32%;
    background: linear-gradient(
        90deg,
        transparent,
        rgb(99 102 241),
        transparent
    );
    animation: cb-risk-sweep 1.4s ease-in-out infinite;
}

.cb-risk-step {
    opacity: 0.38;
    transition:
        opacity 0.28s ease,
        transform 0.28s ease;
}

.cb-risk-step.is-active {
    opacity: 1;
    transform: translateY(-1px);
}

.cb-risk-step-bar {
    width: 0;
    background: rgb(99 102 241);
}

.cb-risk-step.is-active .cb-risk-step-bar {
    animation: cb-risk-fill 1.1s ease-out forwards;
}

.cb-risk-spark-line {
    stroke-dasharray: 180;
    stroke-dashoffset: 180;
    animation: cb-risk-draw 2.4s ease-in-out infinite;
}

@keyframes cb-risk-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes cb-risk-ping {
    75%,
    100% {
        transform: scale(2.4);
        opacity: 0;
    }
}

@keyframes cb-risk-sweep {
    0% {
        transform: translateX(-120%);
    }
    100% {
        transform: translateX(420%);
    }
}

@keyframes cb-risk-fill {
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
}

@keyframes cb-risk-draw {
    0% {
        stroke-dashoffset: 180;
        opacity: 0.35;
    }
    55% {
        stroke-dashoffset: 0;
        opacity: 1;
    }
    85% {
        stroke-dashoffset: 0;
        opacity: 0.2;
    }
    100% {
        stroke-dashoffset: 180;
        opacity: 0.2;
    }
}

@media (prefers-reduced-motion: reduce) {
    .cb-risk-orbit,
    .cb-risk-ping,
    .cb-risk-scan-bar,
    .cb-risk-step-bar,
    .cb-risk-spark-line {
        animation: none;
    }

    .cb-risk-step {
        opacity: 0.7;
        transform: none;
    }

    .cb-risk-step-bar {
        width: 40%;
    }

    .cb-risk-spark-line {
        stroke-dashoffset: 0;
        opacity: 0.6;
    }
}
</style>
